import {
    Component,
    OnInit,
    Input,
    HostListener,
    HostBinding,
    Output,
    EventEmitter,
    OnDestroy,
    ElementRef,
    ViewChild,
} from "@angular/core";
import { of, Subject, Subscription } from "rxjs";
import { debounceTime, distinctUntilChanged, switchMap } from "rxjs/operators";

@Component({
    selector: "app-color-picker",
    templateUrl: "./color-picker.component.html",
    styleUrls: ["./color-picker.component.scss"],
})
export class ColorPickerComponent implements OnInit, OnDestroy {
    constructor(private _el: ElementRef<HTMLElement>) { }

    @Input("type") type: string = "sketch";
    @Input("colors") colors = [
        '#5E4EAE', '#0056CA', '#226F4D', '#206B82', '#815E04', '#B02E21', '#596772',
        '#9F90EF', '#559DFF', '#4BCE98', '#6AC4DF', '#FFA360', '#F96E67', '#8B9BAB'
    ];
    // Nome da cor, exibido no título da folha de seleção no mobile
    @Input() label = 'Cor';

    // Amostras maiores no painel (mesmas do ngx-color, sem as transparentes)
    readonly panelPresetColors = [
        '#D0021B', '#F5A623', '#F8E71C', '#8B572A', '#7ED321', '#417505', '#BD10E0',
        '#9013FE', '#4A90E2', '#50E3C2', '#B8E986', '#000000', '#4A4A4A', '#9B9B9B', '#FFFFFF',
    ];

    public isVisible: boolean = false;
    public selectedColor = "#559DFF";
    // Cor em edição no painel; só vira selectedColor ao tocar em "Aplicar"
    public draftColor = "#559DFF";

    // Web: posição do painel (popover) junto da amostra
    @ViewChild('panel') private panelRef?: ElementRef<HTMLElement>;
    public popoverTop = 0;
    public popoverLeft = 0;
    public popoverReady = false;
    private readonly repositionListener = () => this.positionPopover();
    private readonly changeSubject = new Subject<string | undefined>();
    private changeSubscription?: Subscription;

    ngOnInit(): void {
        this.changeSubscription = this.changeSubject
            .pipe(
                // debounceTime(100),
                distinctUntilChanged(),
                switchMap(() => {
                    this.colorChange.emit(this.selectedColor);
                    return of(null);
                })
            )
            .subscribe();
    }

    ngOnDestroy(): void {
        this.changeSubscription?.unsubscribe();
        this.stopRepositioning();
    }

    @Output()
    public colorChange: EventEmitter<string> = new EventEmitter();

    // Cor provisória enquanto o usuário escolhe no painel (para prévia ao vivo)
    @Output()
    public colorPreview: EventEmitter<string> = new EventEmitter();

    @Input()
    public set color(color: string) {
        this.selectedColor = color;
    }

    @HostBinding("style.background-color")
    public get background(): string {
        return this.selectedColor;
    }

    // Tipo "sketch": painel com prévia e Aplicar/Cancelar (o "twitter" das tags segue simples)
    @HostBinding("class.panel")
    public get usesPanel(): boolean {
        return this.type === 'sketch';
    }

    // Painel como folha inferior em telas lt-md; na web, popover junto da amostra
    @HostBinding("class.touch")
    public get isTouch(): boolean {
        return this.usesPanel && typeof window !== 'undefined' && window.innerWidth <= 959;
    }

    @HostBinding("attr.role") readonly role = "button";
    @HostBinding("attr.tabindex") readonly tabindex = 0;
    @HostBinding("attr.aria-label")
    public get ariaLabel(): string {
        return `${this.label}: ${this.selectedColor || 'sem cor'}`;
    }

    @HostListener("keydown.enter", ["$event"])
    @HostListener("keydown.space", ["$event"])
    @HostListener("click", ["$event"])
    public showColorPicker(event: Event) {
        if (this.isVisible === true) {
            return;
        }
        event.preventDefault();
        this.draftColor = this.selectedColor;
        this.isVisible = !this.isVisible;
        if (this.usesPanel && !this.isTouch) {
            this.popoverReady = false;
            // espera o painel renderizar para medir a altura
            setTimeout(() => this.positionPopover());
            window.addEventListener('resize', this.repositionListener);
            window.addEventListener('scroll', this.repositionListener, true);
        }
    }

    @HostListener("document:keydown.escape", ["$event"])
    public onEscape(event: Event) {
        if (this.isVisible && this.usesPanel) {
            this.cancelDraft(event);
        }
    }

    // Abaixo da amostra; sem espaço, acima; sem espaço na vertical, ao lado (direita, depois
    // esquerda), para não cobrir a amostra. Sempre dentro da tela.
    private positionPopover(): void {
        const panel = this.panelRef?.nativeElement;
        if (!this.isVisible || !panel || this.isTouch) return;
        const margin = 8;
        const gap = 8;
        const anchor = this._el.nativeElement.getBoundingClientRect();
        const { offsetWidth: w, offsetHeight: h } = panel;
        const vw = window.innerWidth;
        const vh = window.innerHeight;
        const clampTop = (t: number) => Math.max(margin, Math.min(t, vh - h - margin));
        const clampLeft = (l: number) => Math.max(margin, Math.min(l, vw - w - margin));

        let top: number;
        let left: number;
        if (anchor.bottom + gap + h <= vh - margin) {
            top = anchor.bottom + gap;
            left = clampLeft(anchor.left);
        } else if (anchor.top - gap - h >= margin) {
            top = anchor.top - gap - h;
            left = clampLeft(anchor.left);
        } else if (anchor.right + gap + w <= vw - margin) {
            top = clampTop(anchor.top + anchor.height / 2 - h / 2);
            left = anchor.right + gap;
        } else if (anchor.left - gap - w >= margin) {
            top = clampTop(anchor.top + anchor.height / 2 - h / 2);
            left = anchor.left - gap - w;
        } else {
            top = clampTop(anchor.bottom + gap);
            left = clampLeft(anchor.left);
        }

        this.popoverTop = Math.round(top);
        this.popoverLeft = Math.round(left);
        this.popoverReady = true;
    }

    private stopRepositioning(): void {
        window.removeEventListener('resize', this.repositionListener);
        window.removeEventListener('scroll', this.repositionListener, true);
    }

    public overlayClick(event: MouseEvent): void {
        event.preventDefault();
        event.stopPropagation();
        if (this.usesPanel) {
            this.cancelDraft(event);
            return;
        }
        this.isVisible = false;
    }

    onColorChanged(event) {
        this.selectedColor = event.color.hex;
        this.changeSubject.next(this.selectedColor);
    }

    onDraftChanged(event) {
        this.draftColor = event.color.hex;
        this.colorPreview.emit(this.draftColor);
    }

    applyDraft(event: Event) {
        event.stopPropagation();
        this.isVisible = false;
        this.stopRepositioning();
        if (this.draftColor && this.draftColor.toLowerCase() !== (this.selectedColor || '').toLowerCase()) {
            this.selectedColor = this.draftColor;
            this.changeSubject.next(this.selectedColor);
        }
    }

    cancelDraft(event: Event) {
        event.stopPropagation();
        this.isVisible = false;
        this.stopRepositioning();
        // Desfaz a prévia ao vivo
        if (this.draftColor !== this.selectedColor) {
            this.colorPreview.emit(this.selectedColor);
        }
        this.draftColor = this.selectedColor;
    }
}
